/**
 * /express — Módulo Express.
 *
 * Acceso: URL directa + PIN. No aparece en el sidebar.
 */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Lock, Plus, FileText, Trash2, ArrowLeft, Copy, AlertTriangle,
  Clock, Wrench, Truck, Banknote, Loader2, RefreshCw, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { deriveKeyFromPin, encryptAmount, decryptAmount, fmtEur } from "@/lib/expressCrypto";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "express_token";

const expressApi = axios.create({ baseURL: `${BACKEND}/api/express` });
expressApi.interceptors.request.use((cfg) => {
  const t = sessionStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers["X-Express-Token"] = t;
  return cfg;
});

const COLS = [
  { key: "aceptado",   label: "Aceptado",      icon: Clock,    color: "amber" },
  { key: "diseno",     label: "En diseño",     icon: FileText, color: "blue" },
  { key: "produccion", label: "En producción", icon: Wrench,   color: "violet" },
  { key: "montado",    label: "Montado",       icon: Truck,    color: "cyan" },
];
const COLORS = {
  amber:  "border-amber-500/40 bg-amber-500/5 text-amber-300",
  blue:   "border-blue-500/40 bg-blue-500/5 text-blue-300",
  violet: "border-violet-500/40 bg-violet-500/5 text-violet-300",
  cyan:   "border-cyan-500/40 bg-cyan-500/5 text-cyan-300",
};

function fmtShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function Express() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(TOKEN_KEY));
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState(false);
  const [authing, setAuthing] = useState(false);
  const [ops, setOps] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // Iter 67 — clave AES-GCM derivada del PIN (vive solo en memoria de la sesión)
  const [cryptoKey, setCryptoKey] = useState(null);
  const [decryptedMap, setDecryptedMap] = useState({});  // {ref → amount}
  const [showAmounts, setShowAmounts] = useState(true);

  const auth = async () => {
    if (!pin) return;
    setAuthing(true); setPinErr(false);
    try {
      const { data } = await axios.post(`${BACKEND}/api/express/auth`, { pin });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      // Derivar la clave AES-GCM del PIN (no se almacena el PIN, solo la CryptoKey)
      try {
        const k = await deriveKeyFromPin(pin);
        setCryptoKey(k);
      } catch (e) {
        console.warn("No se pudo derivar la clave AES:", e);
        setCryptoKey(null);
      }
      setAuthed(true);
      setPin("");
    } catch { setPinErr(true); }
    finally { setAuthing(false); }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setOps([]);
    setSelected(null);
    setCryptoKey(null);
    setDecryptedMap({});
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await expressApi.get("/ops");
      setOps(data.items || []);
    } catch (err) {
      if (err?.response?.status === 404) { logout(); }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) load(); }, [authed]);

  // Descifrar los importes cuando cambian ops o la clave (PIN cambió).
  useEffect(() => {
    if (!cryptoKey) { setDecryptedMap({}); return; }
    let cancelled = false;
    (async () => {
      const out = {};
      for (const o of [...ops, ...historial]) {
        if (!o.importe_cifrado) continue;
        try {
          const v = await decryptAmount(o.importe_cifrado, cryptoKey);
          if (v != null) out[o.ref] = v;
        } catch (_e) { /* ignorar — PIN distinto al de cifrado */ }
      }
      if (!cancelled) setDecryptedMap(out);
    })();
    return () => { cancelled = true; };
  }, [ops, cryptoKey]);

  // Tras refresh de página: si tenemos token de sesión pero no la clave,
  // pedimos al usuario que reintroduzca el PIN para activar el cifrado.
  // (Sin clave: lista de ops sigue funcionando; importes salen como "—".)
  // El banner "Importes ocultos" se muestra debajo (cryptoKey === null).

  if (!authed) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="max-w-sm w-full space-y-4 bg-zinc-900/80 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-2 text-zinc-400">
            <Lock className="w-4 h-4" />
            <span className="text-sm font-mono">Acceso restringido</span>
          </div>
          <Input type="password" placeholder="PIN" value={pin}
            onChange={(e) => { setPin(e.target.value); setPinErr(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") auth(); }}
            className={`text-center tracking-widest text-lg ${pinErr ? "border-red-500" : ""}`}
            data-testid="express-pin-input" autoFocus />
          {pinErr && <div className="text-xs text-red-400">PIN incorrecto</div>}
          <Button onClick={auth} disabled={!pin || authing}
            className="w-full bg-zinc-700 hover:bg-zinc-600 text-zinc-100"
            data-testid="express-pin-submit">
            {authing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
          </Button>
        </div>
      </div>
    );
  }

  // Confirm delete view
  if (confirmDelete && selected) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full space-y-4 bg-red-950/30 border border-red-500/50 rounded-xl p-6">
          <div className="flex items-center gap-2 text-red-300">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-base font-semibold">¿Eliminar la operación?</span>
          </div>
          <div className="text-sm text-zinc-300 space-y-2">
            <p>Vas a eliminar el registro de <b className="font-mono">{selected.ref}</b>. El brief y los datos técnicos se borran de forma permanente.</p>
            <p className="text-emerald-300 text-xs mt-2">
              💶 El importe cifrado se conserva para que las sumas históricas (mensual/anual) no se pierdan.
            </p>
            <p className="text-amber-300 text-xs mt-2">
              ⚠️ Verifica que todo está cerrado antes de continuar. Esta acción no se puede deshacer.
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmDelete(false)} className="flex-1">Cancelar</Button>
            <Button onClick={async () => {
              try {
                await expressApi.delete(`/ops/${selected.ref}`);
                toast.success("Operación eliminada");
                setSelected(null); setConfirmDelete(false); await load();
              } catch { toast.error("Error"); }
            }} className="flex-1 bg-red-600 hover:bg-red-500 text-white"
              data-testid="express-confirm-delete">
              <Trash2 className="w-4 h-4 mr-2" /> Borrar todo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Detail view
  if (selected) {
    return <ExpressDetail op={selected} onBack={() => setSelected(null)}
      onDeleteRequest={() => setConfirmDelete(true)} onReload={load}
      cryptoKey={cryptoKey} decryptedAmount={decryptedMap[selected.ref]} />;
  }

  // ─── Resumen del mes (descifrado en cliente, sin tocar la BD) ───
  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM
  const allOps = [...ops, ...historial];
  const monthlyTotal = allOps.reduce((acc, o) => {
    if ((o.creado_at || "").slice(0, 7) !== currentMonth) return acc;
    const a = decryptedMap[o.ref];
    return acc + (a != null ? a : 0);
  }, 0);
  const annualTotal = allOps.reduce((acc, o) => {
    if ((o.creado_at || "").slice(0, 4) !== String(now.getFullYear())) return acc;
    const a = decryptedMap[o.ref];
    return acc + (a != null ? a : 0);
  }, 0);
  const histConImporte = historial.filter(o => decryptedMap[o.ref] != null).length;
  const opsConImporte = ops.filter(o => decryptedMap[o.ref] != null).length;
  const opsSinImporte = ops.filter(o => o.importe_cifrado && decryptedMap[o.ref] == null).length;

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
              <Lock className="w-5 h-5 text-zinc-400" /> Express
            </h1>
            <div className="text-xs text-zinc-500 font-mono mt-1">
              Trabajos rápidos · brief técnico
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading}
              data-testid="express-refresh">
              <RefreshCw className={"w-3.5 h-3.5 mr-1.5 " + (loading ? "animate-spin" : "")} /> Refrescar
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowNew(true)}
              data-testid="express-new-op">
              <Plus className="w-4 h-4 mr-2" /> Nueva
            </Button>
            <Button size="sm" variant="ghost" onClick={logout} className="text-zinc-400"
              data-testid="express-logout">
              <Lock className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>

        <div className="bg-zinc-900/40 border border-zinc-800 rounded p-3 text-xs text-zinc-400">
          Solo se guarda referencia, estado y brief técnico. Importes opcionales cifrados en el navegador.
        </div>

        {/* Resumen del mes — solo aparece si hay clave para descifrar */}
        {cryptoKey ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="express-pulso">
            <PulsoCard label={`Cobrado en ${currentMonth}`} value={fmtEur(monthlyTotal)}
              hint={`${opsConImporte} op${opsConImporte !== 1 ? "s" : ""} con importe`}
              show={showAmounts} />
            <PulsoCard label={`Cobrado en ${now.getFullYear()}`} value={fmtEur(annualTotal)}
              hint={histConImporte > 0 ? `Suma anual · incluye ${histConImporte} borrada${histConImporte !== 1 ? "s" : ""}` : "Suma anual"}
              show={showAmounts} />
            <PulsoCard label="Total operaciones" value={ops.length}
              hint={`${opsSinImporte} con importe oculto (otro PIN)`} show />
            <button
              type="button"
              onClick={() => setShowAmounts(v => !v)}
              data-testid="express-toggle-amounts"
              className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 hover:bg-zinc-900 transition-colors text-left flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Mostrar importes</div>
                <div className="text-sm text-zinc-200 mt-0.5 font-semibold">{showAmounts ? "Visible" : "Oculto"}</div>
              </div>
              {showAmounts ? <Eye className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-zinc-500" />}
            </button>
          </div>
        ) : (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-400">
            🔐 Importes ocultos · vuelve a entrar con el PIN para verlos en esta sesión.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {COLS.map((col) => {
            const colOps = ops.filter(o => o.estado === col.key);
            const Icon = col.icon;
            return (
              <div key={col.key} className={`rounded-lg border ${COLORS[col.color]} overflow-hidden`}
                data-testid={`express-col-${col.key}`}>
                <div className="px-3 py-2 border-b border-current/30 flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-xs uppercase tracking-wider font-semibold flex-1">{col.label}</span>
                  <span className="text-xs font-mono">{colOps.length}</span>
                </div>
                <div className="p-2 space-y-1.5 min-h-[200px]">
                  {colOps.map((o) => {
                    const amount = decryptedMap[o.ref];
                    const hasEncrypted = !!o.importe_cifrado;
                    return (
                      <button key={o.ref} onClick={() => setSelected(o)}
                        data-testid={`express-card-${o.ref}`}
                        className="w-full text-left p-2 rounded bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800/60 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-sm text-zinc-100">{o.ref}</div>
                          {hasEncrypted && (
                            <div className="text-[10px] font-mono"
                                 data-testid={`express-card-importe-${o.ref}`}>
                              {amount != null
                                ? (showAmounts
                                    ? <span className="text-emerald-300">{fmtEur(amount)}</span>
                                    : <span className="text-zinc-600">•••</span>)
                                : <span className="text-zinc-600" title="Cifrado con otro PIN">🔒</span>}
                            </div>
                          )}
                        </div>
                        <div className="text-[10px] text-zinc-400 mt-0.5 line-clamp-2">{o.brief || "(sin brief)"}</div>
                        {o.fecha_montaje && <div className="text-[10px] text-amber-300 mt-1">📅 {o.fecha_montaje}</div>}
                      </button>
                    );
                  })}
                  {colOps.length === 0 && (
                    <div className="text-center text-[10px] text-zinc-700 py-4">vacío</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {ops.find(o => o.estado === "cobrado") && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">🏁 Cobrados — pendientes de borrar</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {ops.filter(o => o.estado === "cobrado").map(o => (
                <button key={o.ref} onClick={() => setSelected(o)}
                  className="text-left p-2 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700">
                  <div className="font-mono text-sm text-zinc-300">{o.ref}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <NewOpDialog open={showNew} onClose={() => setShowNew(false)}
        onCreated={() => { setShowNew(false); load(); }} />
    </div>
  );
}

function ExpressDetail({ op, onBack, onDeleteRequest, onReload, cryptoKey, decryptedAmount }) {
  const [brief, setBrief] = useState(op.brief || "");
  const [fechaMontaje, setFechaMontaje] = useState(op.fecha_montaje || "");
  const [saving, setSaving] = useState(false);
  // Iter 67 — importe cifrado
  const [editAmount, setEditAmount] = useState("");
  const [savingAmount, setSavingAmount] = useState(false);
  const [showAmt, setShowAmt] = useState(true);

  const save = async () => {
    setSaving(true);
    try {
      await expressApi.patch(`/ops/${op.ref}`, { brief, fecha_montaje: fechaMontaje });
      toast.success("Guardado");
      onReload();
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const changeEstado = async (estado) => {
    try {
      await expressApi.patch(`/ops/${op.ref}`, { estado });
      toast.success(`Movido a ${estado}`);
      onReload();
      op.estado = estado;
    } catch { toast.error("Error"); }
  };

  const guardarImporte = async () => {
    if (!cryptoKey) {
      toast.error("Vuelve a entrar con el PIN para cifrar importes");
      return;
    }
    const raw = (editAmount || "").replace(",", ".").trim();
    if (!raw) {
      toast.error("Indica un importe");
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Importe inválido");
      return;
    }
    setSavingAmount(true);
    try {
      const blob = await encryptAmount(n, cryptoKey);
      await expressApi.patch(`/ops/${op.ref}`, { importe_cifrado: blob });
      toast.success("Importe cifrado guardado");
      setEditAmount("");
      onReload();
    } catch (e) {
      console.error(e);
      toast.error("Error cifrando");
    } finally {
      setSavingAmount(false);
    }
  };

  const borrarImporte = async () => {
    if (!window.confirm("¿Borrar el importe cifrado de esta operación?")) return;
    try {
      await expressApi.patch(`/ops/${op.ref}`, { importe_cifrado: "" });
      toast.success("Importe borrado");
      onReload();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <Button onClick={onBack} variant="ghost" size="sm" className="text-zinc-400">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-2xl text-zinc-100">{op.ref}</div>
              <div className="text-xs text-zinc-500 mt-1">Creado: {fmtShort(op.creado_at)}</div>
            </div>
            <Button size="sm" variant="outline"
              onClick={() => { navigator.clipboard.writeText(op.ref); toast.success("Ref copiada"); }}>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
            </Button>
          </div>

          <div className="bg-zinc-900/40 border border-zinc-800 rounded p-3 text-xs text-zinc-400">
            <b>📋 Recordatorio:</b> esta operación sólo guarda referencia, estado y brief técnico.
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Brief técnico (medidas, materiales, tipo)</label>
            <Textarea value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder="Ej: Rótulo retroiluminado 1.5×0.8m, vinilo translúcido amarillo, montaje en fachada con escarpia"
              rows={5} className="text-sm" data-testid="express-brief-input" />
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">📅 Fecha estimada de montaje</label>
            <Input type="date" value={fechaMontaje || ""}
              onChange={(e) => setFechaMontaje(e.target.value)}
              className="w-48" data-testid="express-fecha-input" />
          </div>

          <Button onClick={save} disabled={saving}
            className="bg-zinc-700 hover:bg-zinc-600 text-zinc-100"
            data-testid="express-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar cambios
          </Button>

          {/* ─── Importe (cifrado en navegador) ─── */}
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded p-3 space-y-2"
               data-testid="express-importe-block">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-emerald-200">
                <Banknote className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wider">Importe</span>
              </div>
              <button
                onClick={() => setShowAmt(v => !v)}
                className="text-[10px] text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
                data-testid="express-detail-toggle-amt">
                {showAmt ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                {showAmt ? "Ocultar" : "Ver"}
              </button>
            </div>

            {op.importe_cifrado ? (
              <div className="flex items-center justify-between gap-2 bg-zinc-950/50 border border-zinc-800 rounded p-2">
                <div className="text-2xl font-bold font-mono text-emerald-300" data-testid="express-importe-actual">
                  {!cryptoKey
                    ? <span className="text-zinc-500 text-sm">🔐 Vuelve a entrar con el PIN para ver</span>
                    : decryptedAmount == null
                      ? <span className="text-zinc-500 text-sm">🔒 Cifrado con otro PIN</span>
                      : (showAmt ? fmtEur(decryptedAmount) : "•••")}
                </div>
                <Button size="sm" variant="outline" onClick={borrarImporte}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="express-importe-borrar">
                  <Trash2 className="w-3 h-3 mr-1" /> Borrar
                </Button>
              </div>
            ) : (
              <div className="text-[11px] text-zinc-400">Sin importe registrado.</div>
            )}

            <div className="flex items-center gap-2">
              <Input
                type="text"
                inputMode="decimal"
                placeholder={op.importe_cifrado ? "Reemplazar importe (€)" : "Importe en € (opcional)"}
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                disabled={!cryptoKey}
                className="bg-zinc-950 border-zinc-800 text-sm font-mono"
                data-testid="express-importe-input"
              />
              <Button
                onClick={guardarImporte}
                disabled={!cryptoKey || savingAmount || !editAmount.trim()}
                className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 font-semibold"
                data-testid="express-importe-guardar">
                {savingAmount ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Guardar"}
              </Button>
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed">
              El valor se cifra en este navegador. Si cambias el PIN, no podrás recuperarlo.
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Estado del Kanban</div>
            <div className="grid grid-cols-5 gap-2">
              {[...COLS, { key: "cobrado", label: "Cobrado", icon: Banknote, color: "amber" }].map((c) => (
                <button key={c.key} onClick={() => changeEstado(c.key)}
                  data-testid={`express-estado-${c.key}`}
                  className={`p-2 rounded border text-xs uppercase font-semibold transition-all ${
                    op.estado === c.key ? COLORS[c.color] + " ring-2 ring-current"
                    : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
                  }`}>{c.label}</button>
              ))}
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4 mt-2">
            <Button onClick={onDeleteRequest} variant="outline"
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="express-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Borrar todo rastro
            </Button>
            <div className="text-[10px] text-zinc-600 text-center mt-2">
              Hard delete · sin papelera
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewOpDialog({ open, onClose, onCreated }) {
  const [brief, setBrief] = useState("");
  const [fecha, setFecha] = useState("");
  const [creating, setCreating] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      await expressApi.post("/ops", { brief, fecha_montaje: fecha || null });
      toast.success("Operación creada");
      setBrief(""); setFecha("");
      onCreated();
    } catch { toast.error("Error"); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Nueva operación Express</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Brief técnico</label>
            <Textarea value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder="Medidas, materiales, tipo (sin cliente ni dirección)" rows={4}
              data-testid="express-new-brief" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">📅 Fecha estimada montaje (opcional)</label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              data-testid="express-new-fecha" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={create} disabled={creating}
            className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
            data-testid="express-new-submit">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========== Pulso del negocio (KPI card) ===========
function PulsoCard({ label, value, hint, show = true }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3"
         data-testid={`pulso-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-xl font-bold font-mono text-emerald-300 mt-0.5">
        {show ? value : "•••"}
      </div>
      {hint && <div className="text-[10px] text-zinc-600 mt-0.5">{hint}</div>}
    </div>
  );
}

